import deDE from "antd/locale/de_DE";
import enUS from "antd/locale/en_US";
import itIT from "antd/locale/it_IT";
import dayjs from "dayjs";
import "dayjs/locale/de";
import "dayjs/locale/en";
import "dayjs/locale/it";

import type { SupportedLocale } from ".";

/**
 * `antd/locale/de_DE.js` is one line: `module.exports = require('../lib/locale/de_DE')`
 * — a CommonJS re-export of a transpiled ES module. What a default import gets
 * out of that depends on who does the interop:
 *
 *   - Node and Vite's SSR transform (so: vitest) unwrap it and hand over the
 *     locale object itself.
 *   - The browser bundle hands over the module record, `{ default: locale }`.
 *
 * In the second case every lookup antd makes — `locale.DatePicker`,
 * `locale.Empty`, `locale.Popconfirm` — misses, and antd quietly falls back to
 * its built-in English defaults while rc-picker falls back to "YYYY-MM-DD".
 * Nothing errors, so it looks like a translation gap rather than a broken
 * import, and no test in jsdom can see it.
 *
 * A locale has no `default` key of its own, so the unwrapping is unambiguous.
 */
type LocaleModule = typeof deDE & { default?: typeof deDE };

function unwrap(mod: LocaleModule): typeof deDE {
  return mod.default ?? mod;
}

/**
 * The locale antd's own components read: button texts, the calendar panel, the
 * pagination, the empty state. It travels through a single ConfigProvider in
 * main.tsx.
 */
const ANTD_LOCALES: Record<SupportedLocale, typeof deDE> = {
  de: unwrap(deDE),
  en: unwrap(enUS),
  it: unwrap(itIT),
};

/**
 * Date formats for the *input* side of the pickers.
 *
 * antd's locale files still carry the pre-5.14 `dateFormat`, while rc-picker —
 * the widget behind DatePicker since then — reads `fieldDateFormat` and falls
 * back to "YYYY-MM-DD" when it finds none (rc-picker/hooks/useLocale). So every
 * picker in the application showed and parsed ISO dates no matter which
 * language was chosen; the calendar panel beside it was translated all along,
 * which is what made it look like a formatting quirk rather than a gap.
 *
 * Spelled out with padded day and month, as the old application's forms had it.
 */
const DATE_FORMATS: Record<SupportedLocale, { date: string; month: string }> = {
  de: { date: "DD.MM.YYYY", month: "MM.YYYY" },
  en: { date: "MM/DD/YYYY", month: "MM/YYYY" },
  it: { date: "DD/MM/YYYY", month: "MM/YYYY" },
};

export function antdLocale(locale: SupportedLocale): typeof deDE {
  const base = ANTD_LOCALES[locale];
  const picker = base.DatePicker;
  const { date, month } = DATE_FORMATS[locale];

  if (!picker) return base;

  return {
    ...base,
    DatePicker: {
      ...picker,
      lang: {
        ...picker.lang,
        fieldDateFormat: date,
        fieldDateTimeFormat: `${date} HH:mm`,
        fieldMonthFormat: month,
      },
    },
  };
}

/**
 * The pickers ask dayjs where a week starts and what the months are called;
 * without this it stays on its built-in English whatever antd is told.
 */
export function applyDayjsLocale(locale: SupportedLocale): void {
  dayjs.locale(locale);
}
